import {isBrowser} from '@github-ui/brand-core/utils/platform'
import {useAnalytics} from '@github-ui/use-analytics'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useCallback, useEffect, useEffectEvent, useId, useState} from 'react'

import ProductAnnouncementBanner from './../ProductAnnouncementBanner/ProductAnnouncementBanner'
import ProductAnnouncementModal from './../ProductAnnouncementModal/ProductAnnouncementModal'
import {COPY} from './ProductAnnouncement.data'
import type {
  ProductAnnouncementAnalytics,
  ProductAnnouncementBannerEvent,
  ProductAnnouncementBannerEventPayload,
  ProductAnnouncementData,
  ProductAnnouncementModalEvent,
  ProductAnnouncementModalEventPayload,
} from './ProductAnnouncement.types'

export interface ProductAnnouncementProps {
  id?: string
  data: ProductAnnouncementData
  label?: string
  shouldShowTabCtas?: boolean
  shouldShowMainCta?: boolean
  useLocalStorage?: boolean
  // CSS selector for one or more server-rendered elements (e.g. a Rails-owned
  // banner's CTA button) that should open the modal on click. When set, the
  // component renders modal-only (no React banner) and does NOT auto-open on
  // mount; the modal stays closed until a matching element is clicked. Lets a
  // host page reuse its own banner markup while delegating the modal to React.
  triggerSelector?: string
  // When provided, ProductAnnouncement emits its own visible/click/dismiss analytics
  // (via @github-ui/use-analytics) using each element's `analyticsAction` from the data
  // model. Consumers that instrument manually can omit this and use onBannerEvent/onModalEvent.
  analytics?: ProductAnnouncementAnalytics
  // When provided, ProductAnnouncement POSTs to this path on modal dismissal (server-side
  // notice dismissal). No request is made when absent or when navigating via a CTA href.
  dismissPath?: string
  onClose?: () => void
  // Hosts with server-owned dismissal keep the banner visible until their request succeeds.
  onBannerDismiss?: () => void
  onBannerEvent?: (event: ProductAnnouncementBannerEvent, data?: ProductAnnouncementBannerEventPayload) => void
  onModalEvent?: (event: ProductAnnouncementModalEvent, data?: ProductAnnouncementModalEventPayload) => void
}

const defaultProps: Partial<ProductAnnouncementProps> = {}

// When a host page renders the same modal-only announcement more than once (e.g.
// a Rails `content_for` that is emitted into multiple layout slots), every copy
// mounts its own ProductAnnouncement — and its own modal Portal, which escapes
// the CSS-hidden responsive wrappers. Because the trigger effect binds to *all*
// elements matching `triggerSelector`, a single CTA click would open every copy,
// leaving a duplicate modal behind after one is closed. This module-level
// registry lets the first-mounted instance "claim" a given trigger selector so
// only it owns the modal; later duplicates render no modal and bind no triggers.
const claimedTriggerSelectors = new Map<string, string>()

const ProductAnnouncement = (props: ProductAnnouncementProps) => {
  const initializedProps: ProductAnnouncementProps = {...defaultProps, ...props}
  const {
    id,
    data,
    label,
    shouldShowTabCtas,
    shouldShowMainCta,
    useLocalStorage,
    triggerSelector,
    analytics,
    dismissPath,
    onClose: onCloseProp,
    onBannerDismiss,
    onBannerEvent,
    onModalEvent,
  } = initializedProps

  const {sendAnalyticsEvent} = useAnalytics({optional: true})

  const emitClickAnalytics = useCallback(
    (payload?: ProductAnnouncementModalEventPayload | ProductAnnouncementBannerEventPayload) => {
      if (!analytics || !payload?.analyticsAction) return
      sendAnalyticsEvent('analytics.click', undefined, {
        category: analytics.category,
        action: payload.analyticsAction,
        label: analytics.label ?? '',
        ...(analytics.plan ? {plan: analytics.plan} : {}),
      })
    },
    [analytics, sendAnalyticsEvent],
  )

  const emitVisibleAnalytics = useCallback(() => {
    if (!analytics) return
    sendAnalyticsEvent('analytics.visible', undefined, {
      category: analytics.category,
      action: 'visible',
      label: analytics.label ?? '',
      ...(analytics.plan ? {plan: analytics.plan} : {}),
    })
  }, [analytics, sendAnalyticsEvent])

  const handleBannerEvent = useCallback(
    (event: ProductAnnouncementBannerEvent, payload?: ProductAnnouncementBannerEventPayload) => {
      if (event === 'displayed') emitVisibleAnalytics()
      emitClickAnalytics(payload)
      onBannerEvent?.(event, payload)
    },
    [emitVisibleAnalytics, emitClickAnalytics, onBannerEvent],
  )

  const handleModalEvent = useCallback(
    (event: ProductAnnouncementModalEvent, payload?: ProductAnnouncementModalEventPayload) => {
      if (event === 'opened') emitVisibleAnalytics()
      emitClickAnalytics(payload)
      onModalEvent?.(event, payload)
    },
    [emitVisibleAnalytics, emitClickAnalytics, onModalEvent],
  )

  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const [isBannerVisible, setIsBannerVisible] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  // When a trigger selector is shared by duplicate renders, only the claiming
  // instance owns the modal. Without a trigger selector each instance owns its
  // own (auto-opening) modal as before.
  const instanceId = useId()
  const [isModalOwner, setIsModalOwner] = useState<boolean>(!triggerSelector)

  useEffect(() => {
    if (!isBrowser() || !triggerSelector) return

    const currentOwner = claimedTriggerSelectors.get(triggerSelector)
    if (!currentOwner || currentOwner === instanceId) {
      claimedTriggerSelectors.set(triggerSelector, instanceId)
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsModalOwner(true)
    } else {
      setIsModalOwner(false)
    }

    return () => {
      if (claimedTriggerSelectors.get(triggerSelector) === instanceId) {
        claimedTriggerSelectors.delete(triggerSelector)
      }
    }
  }, [triggerSelector, instanceId])

  const getLocalStorageKey = useCallback(() => `productAnnouncement.${id || window.location.pathname}:dismissed`, [id])

  const onBannerVisibilityChange = useEffectEvent(() => {
    if (isBrowser() && useLocalStorage && !isBannerVisible && isInitialized) {
      localStorage.setItem(getLocalStorageKey(), 'true')
    }
  })

  useEffect(() => {
    onBannerVisibilityChange()
  }, [isBannerVisible, useLocalStorage])

  const onMount = useEffectEvent(() => {
    if (isBrowser()) {
      const isDismissed = useLocalStorage && localStorage.getItem(getLocalStorageKey()) === 'true'

      const announcementHasBanner = 'banner' in data

      if (!isDismissed) {
        if (announcementHasBanner) {
          setIsBannerVisible(true)
        } else if (!triggerSelector) {
          // Modal-only announcements auto-open on mount — unless an external
          // trigger owns opening, in which case the modal stays closed until a
          // matching element is clicked (see the trigger effect below).
          setIsModalOpen(true)
        }
      }

      setIsInitialized(true)
    }
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    onMount()
  }, [])

  const openModalFromTrigger = useEffectEvent((event: Event) => {
    // Let the host CTA open the modal instead of navigating away.
    event.preventDefault()
    setIsModalOpen(true)
    handleBannerEvent('clicked')
  })

  useEffect(() => {
    if (!isBrowser() || !triggerSelector || !isModalOwner) return

    const triggers = Array.from(document.querySelectorAll(triggerSelector))
    if (triggers.length === 0) return

    const handleClick = (event: Event) => openModalFromTrigger(event)
    for (const trigger of triggers) {
      trigger.addEventListener('click', handleClick)
    }

    return () => {
      for (const trigger of triggers) {
        trigger.removeEventListener('click', handleClick)
      }
    }
  }, [triggerSelector, isModalOwner])

  const hasBanner = 'banner' in data

  return (
    <>
      {hasBanner && (
        <ProductAnnouncementBanner
          {...data.banner}
          isVisible={isBannerVisible}
          onClick={() => setIsModalOpen(true)}
          onDismiss={onBannerDismiss ?? (() => setIsBannerVisible(false))}
          aria={COPY.aria.banner}
          onEvent={handleBannerEvent}
        />
      )}

      {isModalOwner && (
        <ProductAnnouncementModal
          {...data.modal}
          label={label || data.modal.label}
          isOpen={isModalOpen}
          onSoftDismiss={() => setIsModalOpen(false)}
          onClose={() => {
            setIsModalOpen(false)
            if (!hasBanner && useLocalStorage && isBrowser()) {
              localStorage.setItem(getLocalStorageKey(), 'true')
            }
            if (analytics) {
              sendAnalyticsEvent('analytics.click', undefined, {
                category: analytics.category,
                action: 'dismiss',
                label: analytics.label ?? '',
                ...(analytics.plan ? {plan: analytics.plan} : {}),
              })
            }
            if (dismissPath && isBrowser()) {
              void verifiedFetch(dismissPath, {method: 'POST'})
            }
            onCloseProp?.()
          }}
          aria={COPY.aria.modal}
          analytics={analytics}
          shouldShowTabCtas={shouldShowTabCtas}
          shouldShowMainCta={shouldShowMainCta}
          onEvent={handleModalEvent}
        />
      )}
    </>
  )
}

export default ProductAnnouncement
